import React, { useEffect, useState } from 'react';
import { HelpCircle, X, CheckCircle2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

const STORAGE_KEY = 'execpresence.tips.dismissed';

function loadDismissed() {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch (_e) {
    return new Set();
  }
}

function persist(set) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(set)));
  } catch (_e) {
    /* ignore */
  }
}

function isDismissed(id) {
  return loadDismissed().has(id);
}

export function resetAllTips() {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new Event('execpresence:tips:reset'));
  }
}

/**
 * Small pulsing dot beside a UI element. Click opens a popover with:
 *  - "What this does" — one-line role
 *  - "What to expect" — bullets describing outcomes
 *  - "Got it" button to dismiss (persisted via localStorage)
 *
 * Tips silently disappear once dismissed. Reset via resetAllTips().
 */
export function OnboardingTip({
  id,
  title,
  what,
  expect = [],
  placement = 'bottom',
  inline = false,
  className,
  testId,
}) {
  const [dismissed, setDismissed] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setDismissed(isDismissed(id));
    const onReset = () => setDismissed(false);
    window.addEventListener('execpresence:tips:reset', onReset);
    return () => window.removeEventListener('execpresence:tips:reset', onReset);
  }, [id]);

  if (dismissed) return null;

  const onGotIt = () => {
    const set = loadDismissed();
    set.add(id);
    persist(set);
    setDismissed(true);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Tip: ${title}`}
          data-testid={testId || `tip-${id}`}
          className={cn(
            'group relative inline-flex items-center justify-center rounded-full',
            'text-primary hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            inline ? 'ml-1 align-middle' : '',
            className,
          )}
          style={{ width: 18, height: 18 }}
        >
          <span
            aria-hidden
            className="absolute inset-0 rounded-full bg-primary/30 animate-ping"
            style={{ animationDuration: '2s' }}
          />
          <HelpCircle size={14} className="relative z-10" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={placement}
        align="start"
        className="w-72 p-0 overflow-hidden"
        data-testid={`tip-content-${id}`}
      >
        <div className="bg-primary text-primary-foreground px-3 py-2 flex items-center justify-between">
          <span className="text-[10px] font-mono-edge tracking-[0.18em] uppercase">Tip · {title}</span>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="opacity-70 hover:opacity-100"
            aria-label="Close"
          >
            <X size={12} />
          </button>
        </div>
        <div className="p-3 space-y-3">
          <p className="text-sm leading-snug">{what}</p>
          {expect.length > 0 && (
            <div>
              <p className="text-[10px] font-mono-edge tracking-[0.16em] uppercase text-muted-foreground mb-1.5">
                What to expect
              </p>
              <ul className="space-y-1">
                {expect.map((line, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs leading-snug">
                    <CheckCircle2 size={12} className="mt-0.5 shrink-0 text-primary" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex items-center justify-end pt-1">
            <Button size="sm" variant="secondary" onClick={onGotIt} data-testid={`tip-${id}-got-it`}>
              Got it
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default OnboardingTip;
